import React from 'react';

/*

--------------- withActiveItem HOC ---------------

- Introduction:

  This HOC is intended to be used in situations where
  we will be dealing with some sort of collection and
  a selected item within that collection. It will provide
  the component wrapped with info about the active item
  and a function to change it, the wrapped component could
  then distribute this props to its children as required.
  One of the children could for instance wrap the 'setActiveItem'
  function with some logic to perform other stuff (like async calls)
  to implement more complex actions.

- Usage

  Our HOC is actually wrapped into another high order function so in order
  to use it we need to do it the 'react-redux connect fashion'

  withActiveItem(mapActiveItemToProps)(WrappedComponent);

- mapActiveItemToProps

  This is a function that we could use to do some configurations when
  using the HOC, for now the only possible thing to configure is the default
  active item (if any). The function will be provided with the props being
  passed to the WrappedComponent, an example of its implementation will be:

  const mapActiveItemToProps = ownProps => ({
    initialActiveItem: ownProps.collection[0],
  });

  If this function is not implemented the default item selected will be undefined
  And the components being wrapped should handle this situation

- Props provided to the WrappedComponent

  . activeItem:     Active Item within the collection at any time

  . setActiveItem:  Function that could be invoked to change the Active Item,
                    It should receive the item we're making active as its
                    only argument

----------------------------------------------------------

*/

const defaultActiveItem = () => ({
  initialActiveItem: undefined,
});

export default (mapActiveItemToProps = defaultActiveItem) => (WrappedComponent) => {
  class WithActiveItem extends React.Component {
    constructor(props) {
      super(props);

      // Obtain initial values from
      // the props that are being provided
      // to the WrappedComponent
      const {
        initialActiveItem,
      } = mapActiveItemToProps(props);
      // ----------------------------

      this.state = {
        activeItem: initialActiveItem,
      };

      this.setActiveItem = this.setActiveItem.bind(this);
    }

    setActiveItem(item) {
      const { activeItem } = this.state;

      if (activeItem === item) {
        return;
      }

      this.setState({
        activeItem: item,
      });
    }

    render() {
      const { activeItem } = this.state;

      return (
        <WrappedComponent
          {...this.props}
          activeItem={activeItem}
          setActiveItem={this.setActiveItem}
        />
      );
    }
  }

  return WithActiveItem;
};
